import { useEffect, useRef } from 'react';
import { AddVehicleProperties, NewVehicle, SpecData, SpgEngine, SpgModel, SpgOption } from 'src/models';
import { buildSpgEnginesKey, buildSpgModelsKey, buildSpgOptionsKey } from 'src/store/states/SpgVehicle/SpgVehicleSlice';

interface IUseNoVinSpgFlowArgs {
    vehicleSpecs?: SpecData;
    vehicleBeingAdded: NewVehicle;
    onAddNewVehicle: (state: NewVehicle, nextProperty?: AddVehicleProperties) => void;
    spgModels?: SpgModel[];
    spgModelsBuilt?: boolean;
    spgModelsKey?: string;
    getSpgModels?: (make: string, model: string, year: string) => void;
    spgOptions?: SpgOption[];
    optionsBuilt?: boolean;
    optionsKey?: string;
    getSpgOptions?: (make: string, model: string, year: string, spgModelId: string) => void;
    spgEngines?: SpgEngine[];
    enginesBuilt?: boolean;
    enginesKey?: string;
    getSpgEngines?: (make: string, model: string, year: string, spgModelId: string, optionId: string) => void;
    clearSpgVehicle?: () => void;
    // Desktop steps through one pane at a time via `currentProperty`; mobile shows every
    // resolved pane as a stacked accordion and has no such latch — omit `currentProperty` there.
    currentProperty?: AddVehicleProperties;
}

// Shared no-VIN SPG Model/Option/Engine picker chain used by both the desktop (PropertyOptions)
// and mobile (MobileView) AddVehicle panes. See PMG 673005 design doc CPM283981.
export function useNoVinSpgFlow(args: IUseNoVinSpgFlowArgs) {
    const {
        vehicleSpecs, vehicleBeingAdded, onAddNewVehicle,
        spgModels, spgModelsBuilt, spgModelsKey, getSpgModels,
        spgOptions, optionsBuilt, optionsKey, getSpgOptions,
        spgEngines, enginesBuilt, enginesKey, getSpgEngines,
        clearSpgVehicle, currentProperty,
    } = args;
    const { make, model, year, vin, vinOwned, spgModelId, optionId, engineId } = vehicleBeingAdded;

    const vehicleBeingAddedRef = useRef(vehicleBeingAdded);
    vehicleBeingAddedRef.current = vehicleBeingAdded;

    // The SPG Model/Option/Engine selection flow only applies when: no VIN was entered,
    // the "Additional Prompts for SPG Pricing without VIN" spec is on, and at least one Web Op
    // for this profile is SPG priced. VinOwned still counts as "a VIN was entered".
    const isNoVinSpgFlowEnabled = !vin && !vinOwned &&
        vehicleSpecs?.noVinSPGPrompt === 'Y' &&
        vehicleSpecs?.anyWebOpSpgPriced === 'Y';

    // The SPG lists carry the vehicle they were fetched for; ignore data left over from a
    // previously selected vehicle so a stale single option/engine can't auto-skip the pane.
    const spgModelsReady = (spgModelsBuilt ?? false) &&
        (spgModelsKey === undefined || spgModelsKey === buildSpgModelsKey(make, model, year));
    const optionsReady = (optionsBuilt ?? false) &&
        (optionsKey === undefined || optionsKey === buildSpgOptionsKey(make, model, year, spgModelId));
    const enginesReady = (enginesBuilt ?? false) &&
        (enginesKey === undefined ||
            enginesKey === buildSpgEnginesKey(make, model, year, spgModelId, optionId));

    const onSpgModelStep = currentProperty === undefined || currentProperty === 'spgModel';
    const onOptionStep = currentProperty === undefined || currentProperty === 'option';
    const onEngineStep = currentProperty === undefined || currentProperty === 'engine';

    const showSpgModelPane = !!model && onSpgModelStep && spgModelsReady && (spgModels?.length ?? 0) >= 2;
    const showOptionPane = !!model && onOptionStep && optionsReady && (spgOptions?.length ?? 0) >= 2;
    const showEnginePane = !!model && onEngineStep && enginesReady && (spgEngines?.length ?? 0) >= 2;

    // Clear stale SPG vehicle config (options/engines/built flags) when the make or model changes.
    useEffect(function ClearSpgVehicleOnMakeOrModelChange() {
        clearSpgVehicle?.();
    }, [make, model, clearSpgVehicle]);

    // Also clear when the no-VIN SPG flow is disabled (checkbox off, no SPG-priced Web Op,
    // or a VIN entered). Without this, PreviousProperty (NewVehicleSlice) reads leftover
    // spgModels/options/engines array lengths from state.SpgVehicle and lets Back navigation
    // walk into panes that should now be unreachable.
    useEffect(function ClearSpgVehicleWhenFlowDisabled() {
        if (!isNoVinSpgFlowEnabled) {
            clearSpgVehicle?.();
        }
    }, [isNoVinSpgFlowEnabled, clearSpgVehicle]);

    // Fetch SPG models once a model is selected. Skipped when a VIN is present — a VIN decodes
    // the vehicle directly, so there is no make/model/year-based model/option/engine picker to
    // resolve.
    useEffect(function FetchSpgModels() {
        if (isNoVinSpgFlowEnabled && make && model && year && getSpgModels) {
            getSpgModels(make, model, year);
        }
    }, [isNoVinSpgFlowEnabled, make, model, year, getSpgModels]);

    // Resolve the SPG model step: skip when 0, auto-select when exactly 1.
    // An Effect is required here (not fetch-completion logic) because SPG list data arrives
    // asynchronously via Redux props after this component has already rendered — there is no
    // local callback to hook the auto-advance into. Mirrored below for Option/Engine.
    useEffect(function ResolveSpgModelStep() {
        if (!onSpgModelStep || !spgModelsReady) {
            return;
        }
        const mdls = spgModels ?? [];
        if (mdls.length === 0) {
            // With a currentProperty latch (desktop), advancing once is enough — the latch
            // itself stops this Effect from running again. Without one (mobile), only advance
            // when there's actually a stale value to clear, or this would re-dispatch forever.
            const shouldAdvance = currentProperty !== undefined ?
                spgModelId === '' :
                (vehicleBeingAddedRef.current.spgModel !== '' || spgModelId !== '');
            if (shouldAdvance) {
                onAddNewVehicle({ ...vehicleBeingAddedRef.current, spgModel: '', spgModelId: '' }, 'option');
            }
        } else if (mdls.length === 1) {
            // Only auto-select if this model isn't already applied.
            if (spgModelId !== mdls[0].id) {
                onAddNewVehicle({ ...vehicleBeingAddedRef.current, spgModel: mdls[0].desc, spgModelId: mdls[0].id }, 'option');
            }
        }
    }, [onSpgModelStep, spgModelsReady, spgModels, onAddNewVehicle, spgModelId, currentProperty]);

    // Fetch SPG options once the SPG-model step has resolved — i.e. spgModelsReady is true AND
    // either there are no models (0-model vehicle, auto-skipped) OR a model has been chosen.
    // spgModelId may be '' for a 0-model vehicle.
    useEffect(function FetchSpgOptions() {
        if (isNoVinSpgFlowEnabled && make && model && year &&
            spgModelsReady &&
            ((spgModels?.length ?? 0) === 0 || spgModelId !== '') &&
            getSpgOptions) {
            getSpgOptions(make, model, year, spgModelId);
        }
    }, [isNoVinSpgFlowEnabled, make, model, year, spgModelsReady, spgModels, spgModelId, getSpgOptions]);

    // Resolve the option step: skip when 0, auto-select when exactly 1.
    useEffect(function ResolveOptionStep() {
        if (!onOptionStep || !optionsReady) {
            return;
        }
        const opts = spgOptions ?? [];
        if (opts.length === 0) {
            const shouldAdvance = currentProperty !== undefined ?
                optionId === '' :
                (vehicleBeingAddedRef.current.option !== '' || optionId !== '');
            if (shouldAdvance) {
                onAddNewVehicle({ ...vehicleBeingAddedRef.current, option: '', optionId: '' }, 'engine');
            }
        } else if (opts.length === 1) {
            // Only auto-select if this option isn't already applied.
            if (optionId !== opts[0].id) {
                onAddNewVehicle({ ...vehicleBeingAddedRef.current, option: opts[0].desc, optionId: opts[0].id }, 'engine');
            }
        }
    }, [onOptionStep, optionsReady, spgOptions, onAddNewVehicle, optionId, currentProperty]);

    // Fetch engines once an option is chosen (or auto-resolved). Desktop only reaches the
    // 'engine' step once the option step has already resolved, so `onEngineStep` alone would
    // suffice there; mobile has no step latch, so the `optionsReady`/`optionId` check does the
    // equivalent job of confirming the option step is actually done.
    useEffect(function FetchSpgEngines() {
        if (isNoVinSpgFlowEnabled && onEngineStep && make && model && year &&
            optionsReady && ((spgOptions?.length ?? 0) === 0 || optionId !== '') &&
            getSpgEngines) {
            getSpgEngines(make, model, year, spgModelId, optionId);
        }
    }, [isNoVinSpgFlowEnabled, onEngineStep, make, model, year, optionsReady, spgOptions, spgModelId, optionId, getSpgEngines]);

    // Resolve the engine step: skip when 0, auto-select when exactly 1.
    useEffect(function ResolveEngineStep() {
        if (!onEngineStep || !enginesReady) {
            return;
        }
        const engs = spgEngines ?? [];
        if (engs.length === 0) {
            const shouldAdvance = currentProperty !== undefined ?
                engineId === '' :
                (vehicleBeingAddedRef.current.engine !== '' || engineId !== '');
            if (shouldAdvance) {
                onAddNewVehicle({ ...vehicleBeingAddedRef.current, engine: '', engineId: '' }, 'color');
            }
        } else if (engs.length === 1) {
            // Only auto-select if this engine isn't already applied.
            if (engineId !== engs[0].id) {
                onAddNewVehicle({ ...vehicleBeingAddedRef.current, engine: engs[0].name, engineId: engs[0].id }, 'color');
            }
        }
    }, [onEngineStep, enginesReady, spgEngines, onAddNewVehicle, engineId, currentProperty]);

    return { isNoVinSpgFlowEnabled, spgModelsReady, optionsReady, enginesReady, showSpgModelPane, showOptionPane, showEnginePane };
}
